import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminStudents from './pages/admin/AdminStudents';
import AdminStudentTests from './pages/admin/AdminStudentTests';
import AdminTestReview from './pages/admin/AdminTestReview';
import AdminQuestions from './pages/admin/AdminQuestions';
import AdminVideos from './pages/admin/AdminVideos';
import AdminManualReviews from './pages/admin/AdminManualReviews';
import AdminQuestionLookup from './pages/admin/AdminQuestionLookup';
import AdminCards from './pages/admin/AdminCards';
import AdminChapters from './pages/admin/AdminChapters';
import TaughtLog from './pages/admin/TaughtLog';
import StudentDashboard from './pages/student/StudentDashboard';
import DailyTarget from './pages/student/DailyTarget';
import DailyTest from './pages/student/DailyTest';
import DailyPaper from './pages/student/DailyPaper';
import DailyPaperTest from './pages/student/DailyPaperTest';
import DailyPaperResult from './pages/student/DailyPaperResult';
import ChapterTests from './pages/student/ChapterTests';
import TestBuilder from './pages/student/TestBuilder';
import TakeTest from './pages/student/TakeTest';
import TestResult from './pages/student/TestResult';
import TestHistory from './pages/student/TestHistory';
import VideosPage from './pages/student/VideosPage';
import ComingSoon from './pages/student/ComingSoon';

// V2 /app (BRIEFS C2.4) — its own sign-in (device session), separate from the old site.
import { AppAuthProvider } from './context/AppAuthContext';
import AppGate from './components/app/AppGate';
import AppLayout from './components/app/AppLayout';
import AppErrorBoundary from './components/app/AppErrorBoundary';
import AppLogin from './pages/app/AppLogin';
import AppHome from './pages/app/AppHome';
import AppDay from './pages/app/AppDay';
import AppNotesList from './pages/app/AppNotesList';
import AppNotes from './pages/app/AppNotes';
import AppSearch from './pages/app/AppSearch';
import AppPractice from './pages/app/AppPractice';
import AppChapterTest from './pages/app/AppChapterTest';
import AppTest from './pages/app/AppTest';
import AppTestResult from './pages/app/AppTestResult';
import AppTestNotes from './pages/app/AppTestNotes';
import AppRevision from './pages/app/AppRevision';
import AppRevisionSession from './pages/app/AppRevisionSession';
import AppRevisionNotes from './pages/app/AppRevisionNotes';
// V2 admin (BRIEFS C4.4) — inside the existing admin layout and login.
import AdminV2Chapters from './pages/admin/v2/AdminV2Chapters';
import AdminV2Notes from './pages/admin/v2/AdminV2Notes';
import AdminV2Points from './pages/admin/v2/AdminV2Points';
import AdminV2ModuleMap from './pages/admin/v2/AdminV2ModuleMap';
import AdminV2Dictionary from './pages/admin/v2/AdminV2Dictionary';
import AdminV2Clips from './pages/admin/v2/AdminV2Clips';
import AdminV2VideoImport from './pages/admin/v2/AdminV2VideoImport';
import AdminV2VideoApprove from './pages/admin/v2/AdminV2VideoApprove';
import AdminV2Links from './pages/admin/v2/AdminV2Links';
import AdminV2OneLiners from './pages/admin/v2/AdminV2OneLiners';
import AdminV2Pyq from './pages/admin/v2/AdminV2Pyq';
import AdminV2Activity from './pages/admin/v2/AdminV2Activity';
import AdminV2Reports from './pages/admin/v2/AdminV2Reports';
import AdminV2Devices from './pages/admin/v2/AdminV2Devices';
import AdminV2Settings from './pages/admin/v2/AdminV2Settings';
import AdminV2Print from './pages/admin/v2/AdminV2Print';
import AdminV2Structure from './pages/admin/v2/AdminV2Structure';

import AdminLayout from './components/shared/AdminLayout';
import StudentLayout from './components/shared/StudentLayout';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } }
});

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={user.role === 'admin' ? '/admin' : '/student'} />} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="students" element={<AdminStudents />} />
        <Route path="students/:studentId/tests" element={<AdminStudentTests />} />
        <Route path="questions" element={<AdminQuestions />} />
        <Route path="videos" element={<AdminVideos />} />
        <Route path="cards" element={<AdminCards />} />
        <Route path="chapters" element={<AdminChapters />} />
        <Route path="manual-reviews" element={<AdminManualReviews />} />
        <Route path="question-lookup" element={<AdminQuestionLookup />} />
        <Route path="taught-log" element={<TaughtLog />} />
        <Route path="v2" element={<Navigate to="/admin/v2/chapters" replace />} />
        <Route path="v2/chapters" element={<AdminV2Chapters />} />
        <Route path="v2/chapters/:chapterId/structure" element={<AdminV2Structure />} />
        <Route path="v2/notes" element={<AdminV2Notes />} />
        <Route path="v2/points" element={<AdminV2Points />} />
        <Route path="v2/module-map" element={<AdminV2ModuleMap />} />
        <Route path="v2/dictionary" element={<AdminV2Dictionary />} />
        <Route path="v2/clips" element={<AdminV2Clips />} />
        <Route path="v2/videos/import" element={<AdminV2VideoImport />} />
        <Route path="v2/videos/approve" element={<AdminV2VideoApprove />} />
        <Route path="v2/links" element={<AdminV2Links />} />
        <Route path="v2/one-liners" element={<AdminV2OneLiners />} />
        <Route path="v2/pyq" element={<AdminV2Pyq />} />
        <Route path="v2/activity" element={<AdminV2Activity />} />
        <Route path="v2/reports" element={<AdminV2Reports />} />
        <Route path="v2/devices" element={<AdminV2Devices />} />
        <Route path="v2/settings" element={<AdminV2Settings />} />
      </Route>
      <Route path="/admin/test-review/:testId" element={<ProtectedRoute role="admin"><AdminTestReview /></ProtectedRoute>} />
      <Route path="/admin/v2/print" element={<ProtectedRoute role="admin"><AdminV2Print /></ProtectedRoute>} />
      <Route path="/student" element={<ProtectedRoute role="student"><StudentLayout /></ProtectedRoute>}>
        <Route index element={<StudentDashboard />} />
        <Route path="daily-target" element={<DailyTarget />} />
        <Route path="daily-test" element={<DailyTest />} />
        {/* Daily Paper — cumulative, untimed, answers saved as you go */}
        <Route path="daily-paper" element={<DailyPaper />} />
        <Route path="daily-paper/history" element={<DailyPaper />} />
        <Route path="daily-paper/:testId" element={<DailyPaperTest />} />
        <Route path="daily-paper/:testId/result" element={<DailyPaperResult />} />
        <Route path="chapter-tests" element={<ChapterTests />} />
        <Route path="test/new" element={<TestBuilder />} />
        <Route path="test/:id" element={<TakeTest />} />
        <Route path="test/:id/result" element={<TestResult />} />
        <Route path="history" element={<TestHistory />} />
        <Route path="videos" element={<VideosPage />} />
        <Route path="cards" element={<ComingSoon />} />
      </Route>
      {/* V2 /app — new block; old routes above are untouched. */}
      <Route path="/app" element={<AppErrorBoundary><AppAuthProvider><Outlet /></AppAuthProvider></AppErrorBoundary>}>
        <Route path="login" element={<AppLogin />} />
        <Route element={<AppGate />}>
          <Route element={<AppLayout />}>
            <Route index element={<AppHome />} />
            <Route path="day/:day" element={<AppDay />} />
            <Route path="notes" element={<AppNotesList />} />
            <Route path="notes/:chapterId" element={<AppNotes />} />
            <Route path="practice" element={<AppPractice />} />
            <Route path="chapter-test" element={<AppChapterTest />} />
            <Route path="revision" element={<AppRevision />} />
            <Route path="one-liners" element={<Navigate to="/app/revision" replace />} />
            <Route path="search" element={<AppSearch />} />
          </Route>
          <Route element={<AppLayout fullScreen />}>
            <Route path="test/:testId" element={<AppTest />} />
            <Route path="test/:testId/result" element={<AppTestResult />} />
            <Route path="test/:testId/notes" element={<AppTestNotes />} />
            <Route path="revision/:id" element={<AppRevisionSession />} />
            <Route path="revision/:id/notes" element={<AppRevisionNotes />} />
          </Route>
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Route>
      </Route>
      <Route path="/" element={<Navigate to={user ? (user.role === 'admin' ? '/admin' : '/student') : '/login'} />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
